import React, { ReactNode } from 'react'
import { Button, Popover } from 'antd'
import { CloseOutlined } from '@ant-design/icons'
import * as API from '../../../api'
import './index.less'

interface FlatTagProps extends TagProps {
	tagName: string;
}

const normalizeSelectTagsValue = (value: any): TagSelectValueProps => {
	if (!value) return {}
	if (typeof value === 'string') {
		try {
			return JSON.parse(value)
		} catch (e) {
			try {
				return JSON.parse(decodeURIComponent(value))
			} catch (e) {
				return {}
			}
		}
	}
	return value
}

export interface TagValueType {
	content: string;
	[propName: string]: any;
}

export interface TagProps {
	id: number;
	tagKey: string;
	name: string;
	values: TagValueType[];
	direction?: string;
	required?: boolean;
	selectMode?: 'single' | 'multi';
	allowCustom?: boolean;
	customLabel?: string;
	description?: string;
	children?: TagProps[];
}

export interface TagSelectValueProps {
	[propName: string]: string;
}

export interface TagSelectProps {
	list?: TagProps[]; 	// 支持传入标签分类列表
	visibleNum: number; // 可见标签的数量
	readonly?: boolean; // 是否可操作
	value?: TagSelectValueProps;
	btnText?: string; // 【更多】按钮文案
	redisKey?: string; // redis 标签配置 key
	onChange?: (value?: TagSelectValueProps) => void;
}

export interface TagSelectState {
	tagList: TagProps[];
	selectTags: TagSelectValueProps;
	popoverVisible: boolean;
	flatTags: FlatTagProps[]; // 扁平化标签数组
	total: number; // 标签总数
}

class TagSelect extends React.Component<TagSelectProps, TagSelectState> {
	static defaultProps = {
		visibleNum: 5,
		readonly: false,
		btnText: '更多',
		redisKey: 'tagEnum',
	}

	state: TagSelectState = {
		tagList: [],
		selectTags: normalizeSelectTagsValue(this.props.value), // TODO ?
		popoverVisible: false,
		flatTags: [],
		total: 0,
	}

	componentDidMount() {
		if (this.props.list) { // 传入标签
			this.getTagListFromProps()
		} else { // 从接口获取
			this.getTagList()
		}
	}

	componentDidUpdate(prevProps: TagSelectProps, prevState: TagSelectState) {
		// 如果props变化 更新
		const { list: prevList, value: prevValue } = prevProps
		const { list: currentList, value: currentValue } = this.props
		if (prevList !== currentList || JSON.stringify(prevList) !== JSON.stringify(currentList)) {
			this.getTagListFromProps()
		}
		if (prevValue !== currentValue || JSON.stringify(prevValue) !== JSON.stringify(currentValue)) {
			if (!currentValue) return
			const selectTags = this.normalizeSelectTags(currentValue)
			const flatTags = this.getOrderedFlatTags(this.state.tagList, selectTags)
			this.setState({
				selectTags,
				flatTags,
				total: flatTags.length,
			})
		}
	}

	normalizeSelectTags = (value: any): TagSelectValueProps => {
		return normalizeSelectTagsValue(value)
	}

	async getTagList() {
		const res = await API.getRedisValue({ key: this.props.redisKey })
		const { readonly, value } = this.props
		const tagList = this.normalizeTagList(this.getRedisResponseValue(res))
		const list = readonly && value ? this.getReadOnlySelectedTagList(tagList) : tagList
		const flatTags = this.getOrderedFlatTags(list)
		this.setState({
			flatTags,
			total: flatTags.length,
			tagList: list,
		})
	}

	getRedisResponseValue = (res: any): any => {
		if (!res || typeof res !== 'object') return res
		if ('data' in res) return res.data
		if ('value' in res) return res.value
		return res
	}

	normalizeTagList = (data: any): TagProps[] => {
		const rawData = data && typeof data === 'object' && 'value' in data ? data.value : data
		const parsedData = typeof rawData === 'string' ? this.parseTagList(rawData) : rawData
		const list = parsedData && typeof parsedData === 'object' && !Array.isArray(parsedData) && Array.isArray(parsedData.fields)
			? parsedData.fields
			: parsedData

		return Array.isArray(list) ? this.normalizeTagCategories(list) : []
	}

	normalizeTagCategories = (list: any[]): TagProps[] => {
		return list.map((item, index) => {
			const values = Array.isArray(item.values)
				? item.values.map(value => typeof value === 'string' ? { content: value } : value)
				: []

			return {
				...item,
				id: item.id || index + 1,
				values,
				children: Array.isArray(item.children) ? this.normalizeTagCategories(item.children) : undefined,
			}
		})
	}

	parseTagList = (value: string): any => {
		const decodeValue = (data: string) => {
			let result = data
			while (/%[0-9A-Fa-f]{2}/.test(result)) {
				const decoded = decodeURIComponent(result)
				if (decoded === result) break
				result = decoded
			}
			return result
		}

		try {
			return JSON.parse(value)
		} catch (e) {
			try {
				return JSON.parse(decodeValue(value))
			} catch (e) {
				return []
			}
		}
	}

	/**
	 * 生成只读的已选择标签列表
	 * @param fullTagList 全量标签列表
	 */
	getReadOnlySelectedTagList = (fullTagList: TagProps[]): TagProps[] => {
		const selectTags = this.normalizeSelectTags(this.props.value)
		const list: TagProps[] = []
		fullTagList.forEach(item => {
			if (Object.keys(selectTags).includes(item.tagKey)) {
				list.push({
					...item,
					values: item.values.filter(tag => this.isTagSelected(selectTags[item.tagKey], tag.content)),
				})
			}
		})
		return list
	}

	getTagListFromProps() {
		const { list } = this.props
		if (!list) return
		const flatTags = this.getOrderedFlatTags(list)
		this.setState({
			flatTags,
			total: flatTags.length,
			tagList: list,
		})
	}

	getFlatTags = (data: TagProps[]) => {
		const flatTags: FlatTagProps[] = []
		data.forEach((cate: TagProps) => {
			const values: TagValueType[] = Array.isArray(cate.values) ? cate.values : []
			values.forEach(val => {
				flatTags.push({
					tagName: val.content,
					...cate
				})
			})
			if (cate.children && cate.children.length) {
				flatTags.push(...this.getFlatTags(cate.children))
			}
		})
		return flatTags
	}

	getOrderedFlatTags = (data: TagProps[], selectTags = this.state.selectTags) => {
		const flatTags = this.getFlatTags(data)
		const selectedTags: FlatTagProps[] = []
		const unselectedTags: FlatTagProps[] = []

		flatTags.forEach(tag => {
			if (this.isTagSelected(selectTags[tag.tagKey], tag.tagName)) {
				selectedTags.push(tag)
			} else {
				unselectedTags.push(tag)
			}
		})

		return selectedTags.concat(unselectedTags)
	}

	handlePopoverVisibleChange = (visible: boolean): void => {
		this.setState({
			popoverVisible: visible
		})
	}

	getSelectedTagList = (value?: string): string[] => {
		return value ? value.split(',').map(item => item.trim()).filter(Boolean) : []
	}

	isTagSelected = (value: string | undefined, tagName: string): boolean => {
		return this.getSelectedTagList(value).includes(tagName.trim())
	}

	handleTagClick = (tag: FlatTagProps): void => {
		if (this.props.readonly) return

		const { tagKey, tagName, selectMode } = tag
		const tagsSelected = {}

		if (selectMode === 'single') {
			tagsSelected[tagKey] = this.state.selectTags[tagKey] === tagName ? '' : tagName
		} else if (this.state.selectTags[tagKey]) {
			const data = this.state.selectTags[tagKey]
			const selectedTags = this.getSelectedTagList(data)
			tagsSelected[tagKey] = selectedTags.includes(tagName)
				? selectedTags.filter(item => item !== tagName).join(',')
				: selectedTags.concat(tagName).join(',')
		} else {
			tagsSelected[tagKey] = tagName
		}

		const updateSelectTags = Object.assign({}, this.state.selectTags, tagsSelected)
		const flatTags = this.getOrderedFlatTags(this.state.tagList, updateSelectTags)
		this.setState({
			selectTags: updateSelectTags,
			flatTags,
			total: flatTags.length,
		})

		// 触发传入的onChange事件
		const { onChange, value } = this.props
		onChange && onChange({ ...value, ...updateSelectTags })
	}

	handleCustomTagAdd = async (tagCate: TagProps): Promise<void> => {
		if (this.props.readonly) return

		const tagName = window.prompt(`请输入${tagCate.name}标签`)
		if (!tagName || !tagName.trim()) return

		const content = tagName.trim()
		const tagList = this.addCustomTag(this.state.tagList, tagCate.tagKey, content)
		const flatTags = this.getOrderedFlatTags(tagList)
		this.setState({
			tagList,
			flatTags,
			total: flatTags.length,
		}, () => {
			this.handleTagClick({
				tagName: content,
				...tagCate,
			})
		})

		if (!this.props.list) {
			await API.setRedisValue({
				key: this.props.redisKey,
				value: {
					schemaVersion: '1.1',
					fields: tagList,
				},
			})
		}
	}

	addCustomTag = (tagList: TagProps[], tagKey: string, content: string): TagProps[] => {
		return tagList.map(tagCate => {
			if (tagCate.tagKey === tagKey) {
				const values = tagCate.values || []
				if (values.some(item => item.content === content)) return tagCate
				return {
					...tagCate,
					values: values.concat({ content }),
				}
			}

			return {
				...tagCate,
				children: tagCate.children ? this.addCustomTag(tagCate.children, tagKey, content) : tagCate.children,
			}
		})
	}

	renderTag = (tag: FlatTagProps, i: number): React.ReactNode => {
		const { tagKey, tagName } = tag
		const cateSelected = this.state.selectTags[tagKey]
		const isActive = this.isTagSelected(cateSelected, tagName)
		return(
			<div
				key={i}
				className={this.props.readonly ? 'content-tag readonly' : isActive ? 'content-tag active' : 'content-tag'}
				onClick={this.handleTagClick.bind(this, tag)}
			>
				{tagName}
			</div>
		)
	}

	getTagDirection = (tagCate: TagProps): string => {
		if (tagCate.direction) return tagCate.direction

		const tips: string[] = []
		if (tagCate.required !== undefined) {
			tips.push(tagCate.required ? '必填' : '非必填')
		}
		if (tagCate.selectMode) {
			tips.push(tagCate.selectMode === 'single' ? '单选' : '可多选')
		}
		if (tagCate.description) {
			tips.push(tagCate.description)
		}

		return tips.join('，')
	}

	renderTagCate = (tagCate: TagProps, i: number, isChild = false): React.ReactNode => {
		const { name, values = [], children = [], allowCustom, customLabel = '+ 标签' } = tagCate
		const direction = this.getTagDirection(tagCate)

		return (
			<div className={isChild ? 'tag-cate tag-cate-child clearfix' : 'tag-cate clearfix'} key={`${tagCate.tagKey}-${i}`}>
				<p className="title">
					{name}
					{direction && <span className="direction">（{direction}）</span>}
				</p>
				{values.map((tag, j) => {
					return this.renderTag({
						tagName: tag.content,
						...tagCate
					}, j)
				})}
				{allowCustom && <div className="content-tag custom-tag" onClick={this.handleCustomTagAdd.bind(this, tagCate)}>{customLabel}</div>}
				{children.map((child, j) => this.renderTagCate(child, j, true))}
			</div>
		)
	}

	renderVisibleTags = (): React.ReactNode => {
		const { flatTags } = this.state
		return flatTags.slice(0, this.props.visibleNum).map((tag, i) => {
			return this.renderTag(tag, i)
		})
	}

	render() {
		const { tagList, popoverVisible, total } = this.state
		const { visibleNum, btnText } = this.props
		return (
			<div className="tag-select clearfix">
				{this.renderVisibleTags()}
				{total > visibleNum && (<Popover
					trigger="click"
					visible={popoverVisible}
					onVisibleChange={this.handlePopoverVisibleChange}
					content={
						<div className="all-tags-popover">
							<CloseOutlined className="icon-close" onClick={this.handlePopoverVisibleChange.bind(this, false)} />
							{tagList.map((tagCate, i) => this.renderTagCate(tagCate, i))}
						</div>
					}
				>
					<Button type="link" className="more">{btnText}</Button>
				</Popover>)}
			</div>
		)
	}
}

export default TagSelect
